import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Bot, ShoppingBag } from 'lucide-react';

export const HowItWorks = () => {
  const steps = [
    {
      icon: Smartphone,
      title: 'Customer Messages Your WhatsApp',
      description: 'They receive your digital menu link. No app download. No waiting on hold. Instant access to your full menu with photos.'
    },
    {
      icon: Bot,
      title: 'AI Takes the Perfect Order',
      description: 'Asks about extras, upsells drinks/sides, confirms delivery address and payment. Like your best employee—but never tired, never makes mistakes.'
    },
    {
      icon: ShoppingBag,
      title: 'Order Arrives in Your Kitchen',
      description: 'Structured order in your dashboard with all details. Customer gets instant confirmation. You focus on cooking great food.'
    }
  ];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12" data-testid="how-it-works-section">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-primary bg-primary/10 px-4 py-2 rounded-full inline-block mb-4">
            Simple, Fast, Effective
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How It Works: Live in 24 Hours
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            You upload your menu once. AI handles everything else. Forever.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto relative">
          <div className="hidden md:block absolute top-1/3 left-0 right-0 h-1 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 -z-10" />

          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="relative"
                data-testid={`step-${index + 1}`}
              >
                <div className="bg-white p-8 rounded-2xl border border-border/50 shadow-sm hover:shadow-md transition-all text-center relative z-10">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {index + 1}
                  </div>

                  <div className="w-20 h-20 mx-auto mb-6 mt-4 rounded-2xl bg-accent/30 flex items-center justify-center">
                    <Icon className="w-10 h-10 text-primary" strokeWidth={1.5} />
                  </div>

                  <h3 className="text-xl font-bold mb-3 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {step.title}
                  </h3>

                  <p className="text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <p className="text-xl font-semibold text-foreground mb-6">
            "I thought I needed 2 more staff members. EasyOrder saved me $2,400/month." — Omar, Shawarma Palace
          </p>
          <a
            href="https://app.easyorder.delivery/informacoes"
            data-testid="cta-get-started-now"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-5 text-xl font-semibold shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2"
          >
            Start 30-Day Free Trial
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;